import { Injectable } from "@angular/core";
import { StoreService } from "../store.service";
import * as CryptoJS from "crypto-js";
import { MaritalStatus } from "../../common.interface";
import moment from "moment";
import { USER_STORE_DATA_ENUM, USER_TYPE_ENUM } from "./user-types";
import { CompanyType } from "src/app/loan-layout/components/employment-verification/employment-verification.interface";
declare let dataLayer: any[];

interface UserProperties {
    user_id: string;
    user_type?: USER_TYPE_ENUM;
    hashed_name: string;
    hashed_emailid: string;
    hashed_mobileno: string;
    user_salary: number;
    maritial_status: string;
    user_gender: string;
    company_name: string;
    company_type: string;
    user_city: string;
    user_address: string;
    bank_name: string;
    bank_branch: string;
    credit_range: string | null;
    user_age: number;
    salary_greater_25k: boolean;
    hashed_work_email: string;
    bank_ifsc: string;
    loan_count: number;
    blacklist_flag: boolean;
    is_cooloff_user: boolean;
    cooloff_end_date: string;
    rewards_balance: number;
    salary_range: string;
    company_mail_id: string;
}

@Injectable({
    providedIn: "root",
})
export class UserPropertiesService {
    private userProperties: Partial<UserProperties> = {};
    private isInitialized = false;

    constructor(private store: StoreService) {
        this.store = store;
    }

    public hashData(data: string): string {
        if (!data) return "";
        return CryptoJS.SHA256(data).toString();
    }

    collectUserProperties(): Partial<UserProperties> {
        const customerDetails = JSON.parse(this.store.get(USER_STORE_DATA_ENUM.customerDetails) || "{}");
        const maritalStatus = this.normalizeMaritalStatus(customerDetails.maritalStatus);
        const loanCount = customerDetails.loanCount || 0;

        this.userProperties = {
            user_id: customerDetails.customerId?.toString() || "",
            user_type: this.getUserType(loanCount),
            hashed_name: this.hashData(customerDetails.customerName || ""),
            hashed_emailid: this.hashData(customerDetails.email),
            hashed_mobileno: this.hashData(customerDetails.mobile),
            maritial_status: maritalStatus,
            user_gender: this.mapGender(customerDetails.gender),
            user_city: customerDetails.city || "",
            credit_range: this.getCreditRange(customerDetails.creditScore) || null,
            user_age: this.calculateAge(customerDetails.dob),
            loan_count: loanCount,
            blacklist_flag: false,
            is_cooloff_user: false,
            cooloff_end_date: "",
            rewards_balance: 0,
            company_name: customerDetails.companyName || "",
            company_mail_id: customerDetails.workEmail ? this.hashData(customerDetails.workEmail) : "",
        };

        // User address (from KYC/profile)
        if (customerDetails.address) {
            this.userProperties.user_address = customerDetails.address;
        }

        if (customerDetails.userSalary) {
            this.userProperties.user_salary = customerDetails.userSalary;
            this.userProperties.salary_greater_25k = this.isSalaryGreaterThan25k(customerDetails.userSalary);
        }

        if (customerDetails.companyName) {
            this.userProperties.company_name = customerDetails.companyName;
            this.userProperties.company_type = this.getCompanyType(customerDetails.companyType);
        }

        // Company mail id (hashed PII)
        // if (customerDetails.workEmail) {
        //     this.userProperties.hashed_work_email = this.hashData(customerDetails.workEmail);
        // }

        if (customerDetails.bankName) {
            this.userProperties.bank_name = customerDetails.bankName;
            this.userProperties.bank_branch = customerDetails.bankBranch;
        }

        // Blacklist & Cool-off flags (set from dashboard API)
        if (customerDetails.blackListFlag !== undefined) {
            this.userProperties.blacklist_flag = customerDetails.blackListFlag === "0";
            this.userProperties.is_cooloff_user = customerDetails.blackListFlag === "2";
        }
        if (customerDetails.coolOffDate && this.userProperties.is_cooloff_user) {
            this.userProperties.cooloff_end_date = customerDetails.coolOffDate;
        } else {
            this.userProperties.cooloff_end_date = "";
        }
        if (customerDetails.rewardsBalance !== undefined) {
            this.userProperties.rewards_balance = customerDetails.rewardsBalance;
        }
        let salaryRangeValue = this.store.get("monthlyIncome");
        if (salaryRangeValue || customerDetails.salaryRange) {
            this.userProperties.salary_range = salaryRangeValue || "";
        }

        this.isInitialized = true;

        return this.userProperties.hashed_mobileno ? this.userProperties : {};
    }

    updateUserProperty(key: string, value: any): void {
        const userDetailsFromStore = JSON.parse(this.store.get(USER_STORE_DATA_ENUM.customerDetails) || "{}");

        const updatedUserDetails = {
            ...userDetailsFromStore,
            [key]: value,
        };

        this.store.set({
            key: USER_STORE_DATA_ENUM.customerDetails,
            value: JSON.stringify(updatedUserDetails),
        });
        this.refreshUserProperties();
    }

    getFormattedUserProperties(): any {
        const userProperties = this.collectUserProperties();
        this.isInitialized = true;
        return this.filterNullValues(userProperties);
    }

    setUserIdInDataLayer(userId: string): void {
        if (typeof dataLayer !== "undefined") {
            dataLayer.push({
                user_id: userId,
            });
        }
        this.updateUserProperty("user_id", userId);
    }

    setSalaryRangeInDataLayer(salaryRange: string): void {
        if (typeof dataLayer !== "undefined") {
            dataLayer.push({
                salary_range: salaryRange || "",
            });
        }
        this.updateUserProperty("salary_range", salaryRange);
    }

    clearUserIdFromDataLayer(): void {
        if (typeof dataLayer !== "undefined") {
            dataLayer.push({
                user_id: null,
            });
        }
        this.userProperties = {};
        this.isInitialized = false;
    }

    getUserType(loanCount: number): USER_TYPE_ENUM {
        if (loanCount > 0) return USER_TYPE_ENUM.REPEAT_USER;
        return USER_TYPE_ENUM.NEW_USER;
    }

    refreshUserProperties(): void {
        this.isInitialized = false;
        this.collectUserProperties();
    }

    initializeUserProperties(): void {
        this.refreshUserProperties();
    }

    mapGender(gender: string): string {
        if (!gender) return "";
        const g = gender.toLowerCase();
        if (g === "m" || g === "male") return "male";
        if (g === "f" || g === "female") return "female";
        return "other";
    }

    normalizeMaritalStatus(status: string): string {
        if (!status) return "";
        const normalized = status.toLowerCase().trim();
        if (normalized === "single" || normalized === "s" || normalized === "1") return MaritalStatus.SINGLE.name;
        if (normalized === "married" || normalized === "m" || normalized === "2") return MaritalStatus.MARRIED.name;
        if (normalized === "divorced" || normalized === "d" || normalized === "3") return MaritalStatus.DIVORCED.name;
        return status;
    }

    calculateAge(dob: string): number {
        if (!dob) return 0;

        const birthDate = moment(dob);
        const today = moment();

        return today.diff(birthDate, "years");
    }

    private isSalaryGreaterThan25k(salary: number): boolean {
        return Number(salary) > 20000;
    }

    getCreditRange(creditScore: number): string {
        const ranges = [
            { min: 851, max: 10000, label: "Excellent" },
            { min: 806, max: 850, label: "Good" },
            { min: 761, max: 805, label: "Below Average" },
            { min: 300, max: 760, label: "Poor" },
        ];

        const range = ranges.find((r) => creditScore >= r.min && creditScore <= r.max);

        return range?.label || "";
    }

    filterNullValues(obj: any): any {
        if (!obj || typeof obj !== "object") return obj;

        const filtered: any = {};
        Object.keys(obj).forEach((key) => {
            const value = obj[key];

            if (value !== null && value !== undefined && value !== "") {
                filtered[key] = value;
            }
        });

        if (filtered.user_salary === 0) {
            delete filtered.user_salary;
            delete filtered.is_salary_greater_25k;
        }
        if (filtered.user_age === 0) {
            delete filtered.user_age;
            delete filtered.user_type;
        }

        return filtered;
    }

    getCompanyType(companyType: string): string {
        if (!companyType) return "";

        if (companyType === CompanyType.LLP.value) return CompanyType.LLP.name;
        if (companyType === CompanyType.PRIVATE.value) return CompanyType.PRIVATE.name;
        if (companyType === CompanyType.LISTED.value) return CompanyType.LISTED.name;
        if (companyType === CompanyType.GOVT.value) return CompanyType.GOVT.name;
        if (companyType === CompanyType.OTHERS.value) return CompanyType.OTHERS.name;

        return companyType;
    }

    setUserPhoneInCustomerDetails(phone: string): void {
        const customerDetails = JSON.parse(this.store.get(USER_STORE_DATA_ENUM.customerDetails) || "{}");
        customerDetails.mobile = phone;
        this.store.set({
            key: USER_STORE_DATA_ENUM.customerDetails,
            value: JSON.stringify(customerDetails),
        });
        this.collectUserProperties();
    }

    setUserLoanCountInCustomerDetails(loanCount: number): void {
        const customerDetails = JSON.parse(this.store.get(USER_STORE_DATA_ENUM.customerDetails) || "{}");
        customerDetails.loanCount = loanCount;
        this.store.set({
            key: USER_STORE_DATA_ENUM.customerDetails,
            value: JSON.stringify(customerDetails),
        });
        this.collectUserProperties();
    }

    /**
     * Set dashboard-derived user properties (blackListFlag, coolOffDate, loanCount, rewardsBalance)
     * Call this from getDashboardDetails$ after receiving the dashboard API response.
     */
    setDashboardUserProperties(dashboardData: any): void {
        const customerDetails = JSON.parse(this.store.get(USER_STORE_DATA_ENUM.customerDetails) || "{}");

        // Loan count
        if (dashboardData?.loanCount !== undefined) {
            customerDetails.loanCount = dashboardData.loanCount;
        }

        // Rewards balance
        if (dashboardData?.rewardWalletBalance !== undefined) {
            customerDetails.rewardsBalance = dashboardData.rewardWalletBalance;
        }

        // Blacklist flag & Cool-off (from data.data)
        const innerData = dashboardData?.data;
        if (innerData?.blackListFlag !== undefined) {
            customerDetails.blackListFlag = innerData.blackListFlag;
        } else {
            customerDetails.blackListFlag = false;
        }
        if (innerData?.coolOffDate) {
            customerDetails.coolOffDate = innerData.coolOffDate;
        } else {
            customerDetails.coolOffDate = "";
        }

        this.store.set({
            key: USER_STORE_DATA_ENUM.customerDetails,
            value: JSON.stringify(customerDetails),
        });
        this.collectUserProperties();
    }

    deleteUserProperties(listOfKeys: string[]): void {
        const customerDetails = JSON.parse(this.store.get(USER_STORE_DATA_ENUM.customerDetails) || "{}");
        listOfKeys.forEach((key) => {
            delete customerDetails[key];
        });
        this.store.set({
            key: USER_STORE_DATA_ENUM.customerDetails,
            value: JSON.stringify(customerDetails),
        });
        this.collectUserProperties();
    }
}
